import React, { useState } from 'react';
import { useAtom } from 'jotai';
import { invoicesDataAtom, invoiceDateRangeAtom } from '../utils/atoms';
import { fetchInvoices } from '../utils/requests/invoices';
import { Icon } from '@iconify/react/dist/iconify.js';

const FinanceStats = () => {
    const [invoicesData, setInvoicesData] = useAtom(invoicesDataAtom);
    const [, setDateRange] = useAtom(invoiceDateRangeAtom);
    const [loading, setLoading] = useState(false);
    const [selectedPeriod, setSelectedPeriod] = useState('current-month');

    const { documents = [], expenses = [] } = invoicesData || {};

    // Calculate totals
    const totalInvoices = documents.length;
    const totalExpenses = expenses.length;
    const sumInvoices = documents.reduce((sum, doc) => sum + (Number(doc.Total) || 0), 0);
    const sumExpenses = expenses.reduce((sum, exp) => sum + (Number(exp.total) || 0), 0);
    const netIncome = sumInvoices - sumExpenses;

    // Period selection handlers
    const handlePeriodChange = async (period) => {
        setSelectedPeriod(period);
        setLoading(true);

        let startDate, endDate;
        const now = new Date();

        switch (period) {
            case 'current-month':
                startDate = new Date(now.getFullYear(), now.getMonth(), 1);
                endDate = new Date(now.getFullYear(), now.getMonth() + 1, 0);
                break;
            case 'last-month':
                startDate = new Date(now.getFullYear(), now.getMonth() - 1, 1);
                endDate = new Date(now.getFullYear(), now.getMonth(), 0);
                break;
            case 'current-year':
                startDate = new Date(now.getFullYear(), 0, 1);
                endDate = new Date(now.getFullYear(), 11, 31);
                break;
            default:
                startDate = new Date(now.getFullYear(), now.getMonth(), 1);
                endDate = new Date(now.getFullYear(), now.getMonth() + 1, 0);
        }

        try {
            const data = await fetchInvoices(startDate.getTime(), endDate.getTime());
            setInvoicesData(data);
            setDateRange([startDate, endDate]);
        } catch (error) {
            console.error('Error fetching invoices:', error);
        } finally {
            setLoading(false);
        }
    };

    return (
        <div className="mt-5">
            {/* Period Selection */}
            <div className="mb-4">
                <div className="mb-20">
                    <h5 className="mb-0">סטטיסטיקות פיננסיות</h5>
                </div>
                <div>
                    <div className="d-flex flex-wrap gap-2" style={{ marginBottom: 48 }}>
                        <button
                            type="button"
                            className={`btn ${selectedPeriod === 'current-month' ? 'btn-primary' : 'btn-outline-primary'}`}
                            onClick={() => handlePeriodChange('current-month')}
                            disabled={loading}
                        >
                            {loading && selectedPeriod === 'current-month' && (
                                <span className="spinner-border spinner-border-sm me-2" role="status" />
                            )}
                            החודש הנוכחי
                        </button>
                        <button
                            type="button"
                            className={`btn ${selectedPeriod === 'last-month' ? 'btn-primary' : 'btn-outline-primary'}`}
                            onClick={() => handlePeriodChange('last-month')}
                            disabled={loading}
                        >
                            {loading && selectedPeriod === 'last-month' && (
                                <span className="spinner-border spinner-border-sm me-2" role="status" />
                            )}
                            החודש שעבר
                        </button>
                        <button
                            type="button"
                            className={`btn ${selectedPeriod === 'current-year' ? 'btn-primary' : 'btn-outline-primary'}`}
                            onClick={() => handlePeriodChange('current-year')}
                            disabled={loading}
                        >
                            {loading && selectedPeriod === 'current-year' && (
                                <span className="spinner-border spinner-border-sm me-2" role="status" />
                            )}
                            השנה הנוכחית
                        </button>
                    </div>

                    {/* Stats Cards */}
                    <div className="row g-4 mb-20">
                        <div className="col-md-3 col-6">
                            <div className="card text-center p-3">
                                <Icon icon="mdi:file-document-outline" className="text-primary mb-2" style={{ fontSize: 32 }} />
                                <div className="fw-bold fs-4">{totalInvoices}</div>
                                <div className="text-secondary">חשבוניות</div>
                            </div>
                        </div>
                        <div className="col-md-3 col-6">
                            <div className="card text-center p-3">
                                <Icon icon="mdi:cash-multiple" className="text-success mb-2" style={{ fontSize: 32 }} />
                                <div className="fw-bold fs-4">₪{sumInvoices.toLocaleString('he-IL')}</div>
                                <div className="text-secondary">סך הכל הכנסות</div>
                            </div>
                        </div>
                        <div className="col-md-3 col-6">
                            <div className="card text-center p-3">
                                <Icon icon="mdi:receipt-text-outline" className="text-info mb-2" style={{ fontSize: 32 }} />
                                <div className="fw-bold fs-4">{totalExpenses}</div>
                                <div className="text-secondary">הוצאות</div>
                            </div>
                        </div>
                        <div className="col-md-3 col-6">
                            <div className="card text-center p-3">
                                <Icon icon="mdi:bank-minus" className="text-danger mb-2" style={{ fontSize: 32 }} />
                                <div className="fw-bold fs-4">₪{sumExpenses.toLocaleString('he-IL')}</div>
                                <div className="text-secondary">סך הכל הוצאות</div>
                            </div>
                        </div>
                    </div>

                    {/* Net Income Card */}
                    <div className="row mb-4">
                        <div className="col-12">
                            <div className="card text-center p-4 pb-20">
                                <Icon icon="mdi:trending-up" className={`mb-2 ${netIncome >= 0 ? 'text-success' : 'text-danger'}`} style={{ fontSize: 48 }} />
                                <div className={`fw-bold fs-2 ${netIncome >= 0 ? 'text-success' : 'text-danger'}`}>₪{netIncome.toLocaleString('he-IL')}</div>
                                <div className="text-secondary fs-5">רווח</div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </div >
    );
};

export default FinanceStats; 